import { useState, useEffect, useRef } from 'react';
import { Users, DollarSign, MessageSquare, Settings, UserPlus, TrendingUp, ChevronDown, ChevronRight, ChevronUp, Mail, MessageCircle, Tag, Pause, Archive, Play, Ticket, Brain, Plus, Upload, Search, ToggleLeft, ToggleRight, Pencil, Trash2, FileText, X, Save, Eye, EyeOff, ChevronLeft, Link2, Send, Bot, AlertTriangle, RefreshCw, IndianRupee, BarChart3, Palette, Check } from 'lucide-react';
import { adminApi, offerApi, couponApi, knowledgeApi } from '../services/api';
import RevenueTab from '../components/RevenueTab';
import MarkdownRenderer from '../components/common/MarkdownRenderer';
import toast from 'react-hot-toast';
import { PALETTES } from '../theme/palettes';
import { setActivePalette } from '../theme/apply';

const getWhatsAppUrl = (phone) => {
  if (!phone) return null;
  let cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('0')) cleaned = '91' + cleaned.slice(1);
  if (cleaned.length === 10) cleaned = '91' + cleaned;
  return `https://wa.me/${cleaned}`;
};

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [students, setStudents] = useState([]);
  const [config, setConfig] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [stuckPayments, setStuckPayments] = useState([]);
  const [recoveringId, setRecoveringId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashRes, trainersRes, configRes, stuckRes] = await Promise.all([
        adminApi.getDashboard(),
        adminApi.getTrainers(),
        adminApi.getConfig(),
        adminApi.getStuckPayments().catch(() => ({ data: [] }))
      ]);
      setStats(dashRes.data);
      setTrainers(trainersRes.data);
      setConfig(configRes.data);
      setStuckPayments(stuckRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecover = async (payment) => {
    setRecoveringId(payment.id);
    try {
      const res = await adminApi.recoverPayment(payment.id);
      toast.success(res.data.message || 'Payment recovered');
      setStuckPayments(prev => prev.filter(p => p.id !== payment.id));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Recovery failed');
    } finally {
      setRecoveringId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: TrendingUp },
    { id: 'revenue', name: 'Revenue', icon: IndianRupee },
    { id: 'trainers', name: 'Trainers', icon: Users },
    { id: 'students', name: 'Students', icon: Users },
    { id: 'knowledge', name: 'AI Knowledge', icon: Brain },
    { id: 'offers', name: 'Offers', icon: Tag },
    { id: 'coupons', name: 'Coupons', icon: Ticket },
    { id: 'ai-usage', name: 'AI Usage', icon: BarChart3 },
    { id: 'referrals', name: 'Referrals', icon: Link2 },
    { id: 'theme', name: 'Theme', icon: Palette },
    { id: 'settings', name: 'Settings', icon: Settings },
  ];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Admin Dashboard</h1>

      {/* Tabs */}
      <div className="flex space-x-1 border-b mb-6 overflow-x-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {tab.name}
            </button>
          );
        })}
      </div>

      {/* Stuck Payments Banner — only on overview tab */}
      {activeTab === 'overview' && stuckPayments.length > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="font-semibold text-amber-800">{stuckPayments.length} stuck payment{stuckPayments.length > 1 ? 's' : ''} waiting for recovery</span>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {stuckPayments.map(p => (
              <div key={p.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-amber-100">
                <div className="text-sm">
                  <span className="font-medium text-gray-900">{p.user_name}</span>
                  <span className="text-gray-500 mx-2">|</span>
                  <span className="text-gray-600">{p.payment_type}</span>
                  <span className="text-gray-500 mx-2">|</span>
                  <span className="font-medium text-gray-900">₹{p.amount}</span>
                  {p.questions > 0 && <span className="text-gray-500 ml-1">({p.questions} Q)</span>}
                  <span className="text-gray-400 ml-2 text-xs">{new Date(p.created_at).toLocaleString()}</span>
                </div>
                <button
                  onClick={() => handleRecover(p)}
                  disabled={recoveringId === p.id}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                >
                  <RefreshCw className={`w-3 h-3 ${recoveringId === p.id ? 'animate-spin' : ''}`} />
                  {recoveringId === p.id ? 'Recovering...' : 'Recover'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab stats={stats} />}
      {activeTab === 'revenue' && <RevenueTab />}
      {activeTab === 'trainers' && <TrainersTab trainers={trainers} onRefresh={loadData} />}
      {activeTab === 'students' && <StudentsTab />}
      {activeTab === 'knowledge' && <KnowledgeTab />}
      {activeTab === 'offers' && <OffersTab />}
      {activeTab === 'coupons' && <CouponsTab />}
      {activeTab === 'ai-usage' && <UsageAnalyticsTab />}
      {activeTab === 'referrals' && <ReferralsTab />}
      {activeTab === 'theme' && <ThemeTab config={config} onSave={loadData} />}
      {activeTab === 'settings' && <SettingsTab config={config} onSave={loadData} />}
    </div>
  );
}

function OverviewTab({ stats }) {
  const [expandedCard, setExpandedCard] = useState(null);
  const [drillDownData, setDrillDownData] = useState(null);
  const [revenueMonth, setRevenueMonth] = useState(new Date().toISOString().slice(0, 7));

  const handleStatusChange = async (userId, role, newStatus) => {
    try {
      if (role === 'student') {
        await adminApi.updateStudentStatus(userId, newStatus);
      } else {
        await adminApi.updateTrainerStatus(userId, newStatus);
      }
      // Update local drill-down data
      setDrillDownData(prev => Array.isArray(prev)
        ? prev.map(u => u.id === userId ? { ...u, status: newStatus, is_active: newStatus === 'active' } : u)
        : prev
      );
      toast.success(`User ${newStatus === 'active' ? 'reactivated' : newStatus}`);
    } catch (e) {
      toast.error('Failed to update status');
    }
  };
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  const changeRevenueMonth = async (direction) => {
    const [y, m] = revenueMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + direction, 1);
    const now = new Date();
    if (d > now) return; // don't go into future
    const newMonth = d.toISOString().slice(0, 7);
    setRevenueMonth(newMonth);
    setDrillDownLoading(true);
    try {
      const res = await adminApi.getRevenueBreakdown(newMonth);
      setDrillDownData(res.data);
    } catch (e) { toast.error('Failed to load revenue'); }
    finally { setDrillDownLoading(false); }
  };

  const formatMonth = (ym) => {
    const [y, m] = ym.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[parseInt(m) - 1]} ${y}`;
  };

  const handleCardClick = async (cardId) => {
    if (expandedCard === cardId) { setExpandedCard(null); return; }
    setExpandedCard(cardId);
    setDrillDownLoading(true);
    try {
      let res;
      switch (cardId) {
        case 'students': res = await adminApi.getStudentsSummary(); break;
        case 'trainers': res = await adminApi.getTrainers(); break;
        case 'revenue': res = await adminApi.getRevenueBreakdown(revenueMonth); break;
        case 'questions': res = await adminApi.getUsageBreakdown(); break;
      }
      setDrillDownData(res.data);
    } catch (e) { toast.error('Failed to load details'); }
    finally { setDrillDownLoading(false); }
  };

  return (
    <div>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div onClick={() => handleCardClick('students')} className={`bg-white rounded-xl p-6 border cursor-pointer transition-colors ${expandedCard === 'students' ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200 hover:border-blue-300'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-blue-100 rounded-lg"><Users className="w-6 h-6 text-blue-600" /></div>
            {expandedCard === 'students' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.totalStudents?.count || 0}</p>
          <p className="text-sm text-gray-500">Total Students</p>
        </div>

        <div onClick={() => handleCardClick('trainers')} className={`bg-white rounded-xl p-6 border cursor-pointer transition-colors ${expandedCard === 'trainers' ? 'border-purple-400 ring-1 ring-purple-200' : 'border-gray-200 hover:border-purple-300'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-purple-100 rounded-lg"><Users className="w-6 h-6 text-purple-600" /></div>
            {expandedCard === 'trainers' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.totalTrainers?.count || 0}</p>
          <p className="text-sm text-gray-500">Total Trainers</p>
        </div>

        <div onClick={() => handleCardClick('revenue')} className={`bg-white rounded-xl p-6 border cursor-pointer transition-colors ${expandedCard === 'revenue' ? 'border-green-400 ring-1 ring-green-200' : 'border-gray-200 hover:border-green-300'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-green-100 rounded-lg"><DollarSign className="w-6 h-6 text-green-600" /></div>
            {expandedCard === 'revenue' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
          <p className="text-3xl font-bold text-gray-900">₹{stats?.monthlyRevenue?.total || 0}</p>
          <p className="text-sm text-gray-500">This Month Revenue</p>
        </div>

        <div onClick={() => handleCardClick('questions')} className={`bg-white rounded-xl p-6 border cursor-pointer transition-colors ${expandedCard === 'questions' ? 'border-orange-400 ring-1 ring-orange-200' : 'border-gray-200 hover:border-orange-300'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-orange-100 rounded-lg"><MessageSquare className="w-6 h-6 text-orange-600" /></div>
            {expandedCard === 'questions' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.monthlyQuestions || 0}</p>
          <p className="text-sm text-gray-500">Questions This Month</p>
        </div>
      </div>

      {/* Drill-Down Panel */}
      {expandedCard && (
        <div className="mb-8 bg-white rounded-xl border border-gray-200 overflow-hidden">
          {drillDownLoading ? (
            <div className="p-6 text-center text-gray-500">Loading...</div>
          ) : expandedCard === 'students' && Array.isArray(drillDownData) ? (
            <div>
              <div className="p-4 border-b bg-gray-50"><h3 className="font-semibold text-gray-700">Student List</h3></div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Trainer</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {drillDownData.map((s, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-sm text-gray-900">{s.name}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{s.email}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{s.trainer_name || 'Direct'}</td>
                        <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${s.tier === 'L1' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{s.tier}</span></td>
                        <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${s.status === 'paused' ? 'bg-amber-100 text-amber-700' : s.status === 'archived' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{s.status || 'active'}</span></td>
                        <td className="px-4 py-2">
                          <div className="flex items-center space-x-1">
                            {(!s.status || s.status === 'active') ? (
                              <>
                                <button onClick={() => handleStatusChange(s.id, 'student', 'paused')} title="Pause" className="p-1 text-gray-400 hover:text-amber-600"><Pause className="w-4 h-4" /></button>
                                <button onClick={() => handleStatusChange(s.id, 'student', 'archived')} title="Archive" className="p-1 text-gray-400 hover:text-red-600"><Archive className="w-4 h-4" /></button>
                              </>
                            ) : (
                              <button onClick={() => handleStatusChange(s.id, 'student', 'active')} title="Reactivate" className="p-1 text-gray-400 hover:text-green-600"><Play className="w-4 h-4" /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : expandedCard === 'trainers' && Array.isArray(drillDownData) ? (
            <div>
              <div className="p-4 border-b bg-gray-50"><h3 className="font-semibold text-gray-700">Trainer List</h3></div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Students</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {drillDownData.map((t, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 text-sm text-gray-900">{t.name}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{t.email}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{t.student_count}</td>
                      <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${t.status === 'paused' ? 'bg-amber-100 text-amber-700' : t.status === 'archived' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{t.status || 'active'}</span></td>
                      <td className="px-4 py-2">
                        <div className="flex items-center space-x-1">
                          {(!t.status || t.status === 'active') ? (
                            <>
                              <button onClick={() => handleStatusChange(t.id, 'trainer', 'paused')} title="Pause" className="p-1 text-gray-400 hover:text-amber-600"><Pause className="w-4 h-4" /></button>
                              <button onClick={() => handleStatusChange(t.id, 'trainer', 'archived')} title="Archive" className="p-1 text-gray-400 hover:text-red-600"><Archive className="w-4 h-4" /></button>
                            </>
                          ) : (
                            <button onClick={() => handleStatusChange(t.id, 'trainer', 'active')} title="Reactivate" className="p-1 text-gray-400 hover:text-green-600"><Play className="w-4 h-4" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : expandedCard === 'revenue' && drillDownData ? (
            <div>
              <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                <button onClick={() => changeRevenueMonth(-1)} className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-200 rounded"><ChevronLeft className="w-5 h-5" /></button>
                <h3 className="font-semibold text-gray-700">Revenue Breakdown — {formatMonth(revenueMonth)}</h3>
                <button onClick={() => changeRevenueMonth(1)} disabled={revenueMonth >= new Date().toISOString().slice(0, 7)} className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight className="w-5 h-5" /></button>
              </div>
              <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 border-b">
                <div><p className="text-xs text-gray-500">Total</p><p className="text-lg font-bold">₹{drillDownData.totals?.total || 0}</p></div>
                <div><p className="text-xs text-gray-500">Tokens</p><p className="text-lg font-bold">₹{Math.round(drillDownData.totals?.knobly || 0)}</p></div>
                <div><p className="text-xs text-gray-500">Trainers</p><p className="text-lg font-bold">₹{Math.round(drillDownData.totals?.trainer_total || 0)}</p></div>
                <div><p className="text-xs text-gray-500">Deepak</p><p className="text-lg font-bold">₹{Math.round(drillDownData.totals?.deepak || 0)}</p></div>
              </div>
              {drillDownData.perTrainer?.length > 0 && (
                <div className="border-b">
                  <div className="px-4 py-2 bg-gray-50"><p className="text-xs font-medium text-gray-500 uppercase">Per Trainer</p></div>
                  {drillDownData.perTrainer.map((t, i) => (
                    <div key={i} className="flex justify-between px-4 py-2 border-t border-gray-100">
                      <span className="text-sm text-gray-700">{t.trainer_name}</span>
                      <span className="text-sm text-gray-600">₹{t.total} ({t.recharges} recharges, ₹{Math.round(t.commission)} commission)</span>
                    </div>
                  ))}
                </div>
              )}
              {drillDownData.recharges?.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-gray-50"><p className="text-xs font-medium text-gray-500 uppercase">Recent Recharges</p></div>
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Package</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {drillDownData.recharges.map((r, i) => (
                        <tr key={i}>
                          <td className="px-4 py-2 text-sm text-gray-900">{r.user_name}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{r.package_name}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">₹{r.amount}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{new Date(r.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : expandedCard === 'questions' && Array.isArray(drillDownData) ? (
            <div>
              <div className="p-4 border-b bg-gray-50"><h3 className="font-semibold text-gray-700">Top Users by Questions This Month</h3></div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {drillDownData.map((u, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 text-sm text-gray-900">{u.name}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{u.email}</td>
                      <td className="px-4 py-2"><span className="text-xs capitalize px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{u.role}</span></td>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{u.questions_asked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">No data available</div>
          )}
        </div>
      )}

      {/* Recent Recharges */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Recent Recharges</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {stats?.recentRecharges?.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3 text-sm">
                    <p className="font-medium text-gray-900">{r.user_name}</p>
                    <p className="text-gray-500 text-xs">{r.user_email}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">₹{r.amount}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{r.questions_granted}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TrainersTab({ trainers, onRefresh }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedTrainer, setExpandedTrainer] = useState(null);
  const [trainerStudents, setTrainerStudents] = useState({});
  const [loadingStudents, setLoadingStudents] = useState(null);
  const [addQuestionsTrainer, setAddQuestionsTrainer] = useState(null);
  const [questionsToAdd, setQuestionsToAdd] = useState('');
  const [addingQuestions, setAddingQuestions] = useState(false);

  const handleAddQuestions = async () => {
    const qty = parseInt(questionsToAdd);
    if (!qty || qty < 1) { toast.error('Enter a valid number'); return; }
    setAddingQuestions(true);
    try {
      const res = await adminApi.addTrainerQuestions(addQuestionsTrainer.id, qty);
      toast.success(res.data.message);
      setAddQuestionsTrainer(null);
      setQuestionsToAdd('');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add questions');
    } finally {
      setAddingQuestions(false);
    }
  };

  const handleTrainerStatus = async (trainerId, status, e) => {
    e.stopPropagation();
    const label = status === 'paused' ? 'Pause' : status === 'archived' ? 'Pause & Archive' : 'Reactivate';
    if (!confirm(`${label} this trainer?`)) return;
    try {
      await adminApi.updateTrainerStatus(trainerId, status);
      toast.success(`Trainer ${status === 'active' ? 'reactivated' : status}`);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update trainer status');
    }
  };

  const handleStudentStatus = async (studentId, status) => {
    const label = status === 'paused' ? 'Pause' : status === 'archived' ? 'Pause & Archive' : 'Reactivate';
    if (!confirm(`${label} this student?`)) return;
    try {
      await adminApi.updateStudentStatus(studentId, status);
      toast.success(`Student ${status === 'active' ? 'reactivated' : status}`);
      // Reload the expanded trainer's students
      if (expandedTrainer) {
        const res = await adminApi.getStudents({ trainer_id: expandedTrainer });
        setTrainerStudents(prev => ({ ...prev, [expandedTrainer]: res.data.students || res.data }));
      }
    } catch (error) {
      toast.error('Failed to update student status');
    }
  };

  const toggleTrainer = async (trainerId) => {
    if (expandedTrainer === trainerId) {
      setExpandedTrainer(null);
      return;
    }

    setExpandedTrainer(trainerId);

    // Fetch students if not already loaded
    if (!trainerStudents[trainerId]) {
      setLoadingStudents(trainerId);
      try {
        const res = await adminApi.getStudents({ trainer_id: trainerId });
        setTrainerStudents(prev => ({ ...prev, [trainerId]: res.data.students || res.data }));
      } catch (error) {
        toast.error('Failed to load students');
      } finally {
        setLoadingStudents(null);
      }
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Trainers</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Trainer
        </button>
      </div>

      <div className="space-y-3">
        {/* Trainers List */}
        {trainers.map((trainer) => (
          <div key={trainer.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Trainer Row */}
            <div
              onClick={() => toggleTrainer(trainer.id)}
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-4">
                <div className="text-gray-400">
                  {expandedTrainer === trainer.id ? (
                    <ChevronDown className="w-5 h-5" />
                  ) : (
                    <ChevronRight className="w-5 h-5" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{trainer.name}</p>
                  <p className="text-sm text-gray-500">{trainer.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-6">
                <div className="text-center">
                  <p className="text-lg font-semibold text-primary-600">{trainer.student_count}</p>
                  <p className="text-xs text-gray-500">Students</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-green-600">₹{parseFloat(trainer.total_earnings || 0).toFixed(2)}</p>
                  <p className="text-xs text-gray-500">Earnings</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-orange-600">₹{parseFloat(trainer.recharge_share || 0).toFixed(2)}</p>
                  <p className="text-xs text-gray-500">Recharge Share</p>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); setAddQuestionsTrainer(trainer); setQuestionsToAdd(''); }}
                    title="Add Questions"
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  <a
                    href={`mailto:${trainer.email}`}
                    onClick={(e) => e.stopPropagation()}
                    title="Send Email"
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                  </a>
                  {trainer.phone && (
                    <a
                      href={getWhatsAppUrl(trainer.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title="WhatsApp"
                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                </div>
                <div className="flex items-center space-x-1">
                  {(!trainer.status || trainer.status === 'active') && (
                    <>
                      <button
                        onClick={(e) => handleTrainerStatus(trainer.id, 'paused', e)}
                        title="Pause"
                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                      >
                        <Pause className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleTrainerStatus(trainer.id, 'archived', e)}
                        title="Pause & Archive"
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {(trainer.status === 'paused' || trainer.status === 'archived') && (
                    <button
                      onClick={(e) => handleTrainerStatus(trainer.id, 'active', e)}
                      title="Reactivate"
                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    >
                      <Play className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  trainer.status === 'paused' ? 'bg-amber-100 text-amber-700' :
                  trainer.status === 'archived' ? 'bg-red-100 text-red-700' :
                  trainer.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {trainer.status === 'paused' ? 'Paused' :
                   trainer.status === 'archived' ? 'Archived' :
                   trainer.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {/* Students List (Expanded) */}
            {expandedTrainer === trainer.id && (
              <div className="border-t border-gray-200 bg-gray-50">
                {loadingStudents === trainer.id ? (
                  <div className="p-4 text-center text-gray-500">Loading students...</div>
                ) : trainerStudents[trainer.id]?.length > 0 ? (
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Questions Available</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {trainerStudents[trainer.id].map((student) => (
                        <tr key={student.id} className="bg-white">
                          <td className="px-6 py-3 text-sm text-gray-900">{student.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{student.email}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              student.tier === 'L1' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {student.tier}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{student.available_questions || 0}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {new Date(student.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-1">
                              {(!student.status || student.status === 'active') && (
                                <>
                                  <button
                                    onClick={() => handleStudentStatus(student.id, 'paused')}
                                    title="Pause"
                                    className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                  >
                                    <Pause className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => handleStudentStatus(student.id, 'archived')}
                                    title="Pause & Archive"
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <Archive className="w-3.5 h-3.5" />
                                  </button>
                                </>
                              )}
                              {(student.status === 'paused' || student.status === 'archived') && (
                                <button
                                  onClick={() => handleStudentStatus(student.id, 'active')}
                                  title="Reactivate"
                                  className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                >
                                  <Play className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <a
                                href={`mailto:${student.email}`}
                                title="Send Email"
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <Mail className="w-4 h-4" />
                              </a>
                              {student.phone && (
                                <a
                                  href={getWhatsAppUrl(student.phone)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="WhatsApp"
                                  className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                >
                                  <MessageCircle className="w-4 h-4" />
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-4 text-center text-gray-500">No students onboarded yet</div>
                )}
              </div>
            )}
          </div>
        ))}

        {trainers.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
            No trainers yet. Click "Add Trainer" to create one.
          </div>
        )}
      </div>

      {showAddModal && (
        <AddTrainerModal onClose={() => setShowAddModal(false)} onSuccess={() => {
          setShowAddModal(false);
          onRefresh();
        }} />
      )}

      {addQuestionsTrainer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            <h2 className="text-lg font-bold mb-1">Add Questions</h2>
            <p className="text-sm text-gray-500 mb-4">for {addQuestionsTrainer.name}</p>
            <input
              type="number"
              min="1"
              max="100000"
              value={questionsToAdd}
              onChange={(e) => setQuestionsToAdd(e.target.value)}
              placeholder="Number of questions"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleAddQuestions()}
            />
            <div className="flex space-x-3">
              <button
                onClick={() => setAddQuestionsTrainer(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddQuestions}
                disabled={addingQuestions}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {addingQuestions ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddTrainerModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await adminApi.createTrainer(formData);
      toast.success('Trainer created successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create trainer');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold mb-4">Add New Trainer</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              required
              minLength={6}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">
              {isSubmitting ? 'Creating...' : 'Create Trainer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StudentsTab() {
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [counts, setCounts] = useState({});

  const filters = [
    { key: 'all', label: 'All', countKey: 'all_count' },
    { key: 'registered', label: 'Registered', countKey: 'registered' },
    { key: 'recharged', label: 'Recharged', countKey: 'recharged' },
    { key: 'active', label: 'Active', countKey: 'active' },
    { key: 'inactive', label: 'Inactive', countKey: 'inactive' },
    { key: 'by_trainers', label: 'By Trainers', countKey: 'by_trainers' },
    { key: 'direct', label: 'Direct', countKey: 'direct' },
  ];

  useEffect(() => {
    loadStudents();
    loadCounts();
  }, [activeFilter]);

  const filteredStudents = students.filter((s) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      s.name?.toLowerCase().includes(q) ||
      s.email?.toLowerCase().includes(q) ||
      s.trainer_name?.toLowerCase().includes(q) ||
      s.referral_source?.toLowerCase().includes(q)
    );
  });

  const loadCounts = async () => {
    try {
      const res = await adminApi.getStudentCounts();
      setCounts(res.data);
    } catch (error) {
      console.error('Failed to load counts');
    }
  };

  const loadStudents = async () => {
    try {
      setIsLoading(true);
      // Bumped from 1000 -> 10000 so older students (created before the most recent 1000 signups)
      // still appear in the list. The search box filters client-side, so any record outside this
      // window was invisible. 10000 covers the foreseeable user base without DB strain.
      const params = { limit: 10000 };
      if (activeFilter !== 'all') params.filter = activeFilter;
      const res = await adminApi.getStudents(params);
      setStudents(res.data.students || res.data);
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGrantTrial = async (studentId) => {
    try {
      await adminApi.grantL1Trial(studentId);
      toast.success('L1 trial granted');
      loadStudents();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to grant trial');
    }
  };

  const handleAddQuestions = async (student) => {
    const input = prompt(`Add questions for ${student.name}\nCurrent: ${student.available_questions ?? 0}\n\nEnter number of questions to add:`);
    if (!input) return;
    const questions = parseInt(input);
    if (isNaN(questions) || questions < 1) {
      toast.error('Enter a valid number');
      return;
    }
    try {
      const res = await adminApi.addStudentQuestions(student.id, questions);
      toast.success(res.data.message);
      loadStudents();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add questions');
    }
  };

  const handleStudentStatus = async (studentId, status) => {
    const label = status === 'paused' ? 'Pause' : status === 'archived' ? 'Pause & Archive' : 'Reactivate';
    if (!confirm(`${label} this student?`)) return;
    try {
      await adminApi.updateStudentStatus(studentId, status);
      toast.success(`Student ${status === 'active' ? 'reactivated' : status}`);
      loadStudents();
    } catch (error) {
      toast.error('Failed to update student status');
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200 space-y-3">
        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2">
          {filters.map((f) => {
            const count = counts[f.countKey] ?? '';
            const isActive = activeFilter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`px-3 py-1.5 text-sm rounded-full font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}{count !== '' ? ` (${count})` : ''}
              </button>
            );
          })}
        </div>
        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email or trainer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>
      {/* Horizontal scroll wrapper so mobile users can swipe left/right to see all columns
          (Trainer, Tier, Questions, Status, Actions) instead of only Name+Email fitting. */}
      <div className="overflow-x-auto">
      <table className="w-full min-w-[900px]">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Name</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Email</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Trainer</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Tier</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Questions</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {filteredStudents.map((student) => (
            <tr key={student.id}>
              <td className="px-4 py-3 text-sm font-medium text-gray-900">{student.name}</td>
              <td className="px-4 py-3 text-sm text-gray-600">{student.email}</td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {student.trainer_name ? (
                  <span className="text-primary-700 font-medium">{student.trainer_name}</span>
                ) : 'Direct'}
              </td>
              <td className="px-4 py-3">
                <span className={`text-xs px-2 py-1 rounded-full ${
                  student.tier === 'L1' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                }`}>
                  {student.tier}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">{student.available_questions}</td>
              <td className="px-4 py-3">
                <span className={`text-xs px-2 py-1 rounded-full ${
                  student.status === 'paused' ? 'bg-amber-100 text-amber-700' :
                  student.status === 'archived' ? 'bg-red-100 text-red-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {student.status === 'paused' ? 'Paused' :
                   student.status === 'archived' ? 'Archived' : 'Active'}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleAddQuestions(student)}
                    title="Add Questions"
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                  {student.tier === 'L2' && (
                    <button
                      onClick={() => handleGrantTrial(student.id)}
                      className="text-xs text-primary-600 hover:underline mr-1"
                    >
                      Grant L1
                    </button>
                  )}
                  {(!student.status || student.status === 'active') && (
                    <>
                      <button
                        onClick={() => handleStudentStatus(student.id, 'paused')}
                        title="Pause"
                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                      >
                        <Pause className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleStudentStatus(student.id, 'archived')}
                        title="Pause & Archive"
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Archive className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  {(student.status === 'paused' || student.status === 'archived') && (
                    <button
                      onClick={() => handleStudentStatus(student.id, 'active')}
                      title="Reactivate"
                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    >
                      <Play className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <a
                    href={`mailto:${student.email}`}
                    title="Send Email"
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                  </a>
                  {student.phone && (
                    <a
                      href={getWhatsAppUrl(student.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="WhatsApp"
                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {/* Results count */}
      <div className="px-4 py-3 border-t border-gray-200 text-sm text-gray-500">
        Showing {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''}
        {searchQuery && ` matching "${searchQuery}"`}
      </div>
    </div>
  );
}

function OffersTab() {
  const [offers, setOffers] = useState([]);
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    try {
      const [offersRes, studentsRes] = await Promise.all([
        offerApi.list(),
        adminApi.getStudents({ limit: 200 })
      ]);
      setOffers(offersRes.data);
      setStudents(studentsRes.data.students || studentsRes.data);
    } catch (error) {
      toast.error('Failed to load offers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivate = async (offerId) => {
    try {
      await offerApi.delete(offerId);
      toast.success('Offer deactivated');
      loadOffers();
    } catch (error) {
      toast.error('Failed to deactivate offer');
    }
  };

  const handleSubmitOffer = async (formData) => {
    await offerApi.create(formData);
    toast.success('Offer created');
    setShowCreateModal(false);
    loadOffers();
  };

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Offers</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Tag className="w-4 h-4 mr-2" />
          Create Offer
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purchased</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {offers.map((offer) => (
              <tr key={offer.id}>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{offer.name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">₹{offer.amount}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{offer.questions}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{offer.created_by_name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{offer.recipient_count}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{offer.purchased_count || 0}</td>
                <td className="px-4 py-3">
                  {offer.is_active && (
                    <button
                      onClick={() => handleDeactivate(offer.id)}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Deactivate
                    </button>
                  )}
                  {!offer.is_active && (
                    <span className="text-xs text-gray-500">Inactive</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {offers.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No offers yet. Click "Create Offer" to create one.
          </div>
        )}
      </div>

      {showCreateModal && (
        <AdminCreateOfferModal
          students={students}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleSubmitOffer}
        />
      )}
    </div>
  );
}

function AdminCreateOfferModal({ students, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    amount: '',
    questions: '',
    studentIds: [],
    expiresAt: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleStudent = (id) => {
    setFormData(prev => ({
      ...prev,
      studentIds: prev.studentIds.includes(id)
        ? prev.studentIds.filter(s => s !== id)
        : [...prev.studentIds, id]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.studentIds.length === 0) {
      toast.error('Select at least one student');
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit({
        name: formData.name,
        description: formData.description,
        amount: parseFloat(formData.amount),
        questions: parseInt(formData.questions),
        studentIds: formData.studentIds,
        expiresAt: formData.expiresAt || null
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Create Offer</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Offer Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
              <input
                type="number"
                required
                min={1}
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Questions</label>
              <input
                type="number"
                required
                min={1}
                value={formData.questions}
                onChange={(e) => setFormData({ ...formData, questions: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expires (optional)</label>
            <input
              type="datetime-local"
              value={formData.expiresAt}
              onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Students</label>
            <div className="border border-gray-300 rounded-lg max-h-40 overflow-y-auto p-2 space-y-1">
              {students.map(student => (
                <label key={student.id} className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.studentIds.includes(student.id)}
                    onChange={() => toggleStudent(student.id)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{student.name}</span>
                  <span className="text-xs text-gray-400">{student.email}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">{formData.studentIds.length} selected</p>
          </div>
          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">
              {isSubmitting ? 'Creating...' : 'Create Offer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      const res = await couponApi.list();
      setCoupons(res.data);
    } catch (error) {
      toast.error('Failed to load coupons');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivate = async (couponId) => {
    if (!confirm('Deactivate this coupon?')) return;
    try {
      await couponApi.delete(couponId);
      toast.success('Coupon deactivated');
      loadCoupons();
    } catch (error) {
      toast.error('Failed to deactivate coupon');
    }
  };

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Coupon Codes</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Ticket className="w-4 h-4 mr-2" />
          Create Coupon
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min Purchase</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uses</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Validity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {coupons.map((coupon) => (
                <tr key={coupon.id}>
                  <td className="px-4 py-3">
                    <p className="text-sm font-mono font-bold text-gray-900">{coupon.code}</p>
                    {coupon.description && <p className="text-xs text-gray-500">{coupon.description}</p>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {coupon.discount_type === 'percentage'
                      ? `${coupon.discount_value}%${coupon.max_discount ? ` (max ₹${coupon.max_discount})` : ''}`
                      : `₹${coupon.discount_value}`
                    }
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">₹{coupon.min_purchase || 0}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {coupon.used_count}{coupon.max_uses ? ` / ${coupon.max_uses}` : ' / ∞'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {coupon.valid_to
                      ? `Until ${new Date(coupon.valid_to).toLocaleDateString()}`
                      : 'No expiry'
                    }
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      coupon.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {coupon.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 space-x-3">
                    {coupon.is_active && (
                      <>
                        <button
                          onClick={() => setEditCoupon(coupon)}
                          className="text-sm text-primary-600 hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeactivate(coupon.id)}
                          className="text-sm text-red-600 hover:underline"
                        >
                          Deactivate
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {coupons.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No coupons yet. Click "Create Coupon" to create one.
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateCouponModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); loadCoupons(); }}
        />
      )}

      {editCoupon && (
        <EditCouponModal
          coupon={editCoupon}
          onClose={() => setEditCoupon(null)}
          onSuccess={() => { setEditCoupon(null); loadCoupons(); }}
        />
      )}
    </div>
  );
}

function EditCouponModal({ coupon, onClose, onSuccess }) {
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toISOString().slice(0, 16);
  };

  const [formData, setFormData] = useState({
    discount_type: coupon.discount_type || 'percentage',
    discount_value: coupon.discount_value ?? '',
    min_purchase: coupon.min_purchase ?? '',
    max_discount: coupon.max_discount ?? '',
    max_uses: coupon.max_uses ?? '',
    max_uses_per_user: coupon.max_uses_per_user ?? 1,
    valid_to: formatDateForInput(coupon.valid_to),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.discount_value || parseFloat(formData.discount_value) <= 0) {
      toast.error('Discount value must be greater than 0');
      return;
    }
    if (formData.discount_type === 'percentage' && parseFloat(formData.discount_value) > 100) {
      toast.error('Percentage discount cannot exceed 100%');
      return;
    }
    setIsSubmitting(true);
    try {
      await couponApi.update(coupon.id, {
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        min_purchase: formData.min_purchase ? parseFloat(formData.min_purchase) : 0,
        max_discount: formData.discount_type === 'percentage' && formData.max_discount ? parseFloat(formData.max_discount) : null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        max_uses_per_user: parseInt(formData.max_uses_per_user || 1),
        valid_to: formData.valid_to || null,
      });
      toast.success('Coupon updated');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update coupon');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold mb-1">Edit Coupon</h2>
        <p className="text-sm text-gray-500 font-mono mb-4">{coupon.code}</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
              <select
                value={formData.discount_type}
                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed (₹)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount Value {formData.discount_type === 'percentage' ? '(%)' : '(₹)'}</label>
              <input
                type="number"
                min="1"
                max={formData.discount_type === 'percentage' ? 100 : undefined}
                step="0.01"
                value={formData.discount_value}
                onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Purchase (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.min_purchase}
                onChange={(e) => setFormData({ ...formData, min_purchase: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            {formData.discount_type === 'percentage' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Discount (₹)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.max_discount}
                  onChange={(e) => setFormData({ ...formData, max_discount: e.target.value })}
                  placeholder="No limit"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Total Uses</label>
              <input
                type="number"
                min={coupon.used_count || 1}
                value={formData.max_uses}
                onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                placeholder="Unlimited"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-400 mt-1">Used: {coupon.used_count || 0}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses Per User</label>
              <input
                type="number"
                min={1}
                value={formData.max_uses_per_user}
                onChange={(e) => setFormData({ ...formData, max_uses_per_user: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valid To</label>
            <input
              type="datetime-local"
              value={formData.valid_to}
              onChange={(e) => setFormData({ ...formData, valid_to: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-400 mt-1">Leave empty for no expiry</p>
          </div>
          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateCouponModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_purchase: '',
    max_discount: '',
    max_uses: '',
    max_uses_per_user: '1',
    valid_from: '',
    valid_to: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await couponApi.create({
        ...formData,
        code: formData.code.toUpperCase(),
        discount_value: parseFloat(formData.discount_value),
        min_purchase: formData.min_purchase ? parseFloat(formData.min_purchase) : 0,
        max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        max_uses_per_user: parseInt(formData.max_uses_per_user || 1),
        valid_from: formData.valid_from || null,
        valid_to: formData.valid_to || null
      });
      toast.success('Coupon created');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create coupon');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Create Coupon</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
            <input
              type="text"
              required
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              placeholder="e.g., WELCOME20"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
              <select
                value={formData.discount_type}
                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount (₹)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Value {formData.discount_type === 'percentage' ? '(%)' : '(₹)'}
              </label>
              <input
                type="number"
                required
                min={1}
                value={formData.discount_value}
                onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Purchase (₹)</label>
              <input
                type="number"
                min={0}
                value={formData.min_purchase}
                onChange={(e) => setFormData({ ...formData, min_purchase: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            {formData.discount_type === 'percentage' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Discount (₹)</label>
                <input
                  type="number"
                  min={0}
                  value={formData.max_discount}
                  onChange={(e) => setFormData({ ...formData, max_discount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Total Uses</label>
              <input
                type="number"
                min={1}
                value={formData.max_uses}
                onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                placeholder="Unlimited"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses Per User</label>
              <input
                type="number"
                min={1}
                value={formData.max_uses_per_user}
                onChange={(e) => setFormData({ ...formData, max_uses_per_user: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
              <input
                type="datetime-local"
                value={formData.valid_from}
                onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valid To</label>
              <input
                type="datetime-local"
                value={formData.valid_to}
                onChange={(e) => setFormData({ ...formData, valid_to: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">
              {isSubmitting ? 'Creating...' : 'Create Coupon'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const CONTENT_TYPES = [
  { value: 'technique', label: 'Technique', color: 'bg-blue-100 text-blue-700' },
  { value: 'example', label: 'Example', color: 'bg-green-100 text-green-700' },
  { value: 'style_guide', label: 'Style Guide', color: 'bg-purple-100 text-purple-700' },
  { value: 'terminology', label: 'Terminology', color: 'bg-orange-100 text-orange-700' },
  { value: 'instruction', label: 'Instruction', color: 'bg-red-100 text-red-700' },
  { value: 'general', label: 'General', color: 'bg-gray-100 text-gray-700' },
];

function getTypeBadge(type) {
  const ct = CONTENT_TYPES.find(t => t.value === type) || CONTENT_TYPES[5];
  return <span className={`text-xs px-2 py-0.5 rounded-full ${ct.color}`}>{ct.label}</span>;
}

function KnowledgeTab() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [techniques, setTechniques] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterTechnique, setFilterTechnique] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [uploadState, setUploadState] = useState(null); // null | 'uploading' | { chunks, fileName, storedPath }
  const [uploadTechniqueId, setUploadTechniqueId] = useState('');
  const [showTestChat, setShowTestChat] = useState(false);
  const [testMessages, setTestMessages] = useState([]);
  const [testInput, setTestInput] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testingEntry, setTestingEntry] = useState(null); // null = test all KB, { id, title } = specific entry
  const testChatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [entriesRes, statsRes, techRes] = await Promise.all([
        knowledgeApi.getAll(),
        knowledgeApi.getStats(),
        knowledgeApi.getTechniques()
      ]);
      setEntries(entriesRes.data);
      setStats(statsRes.data);
      setTechniques(techRes.data);
    } catch (error) {
      toast.error('Failed to load knowledge base');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async (formData) => {
    try {
      await knowledgeApi.create(formData);
      toast.success('Entry added');
      setShowAddForm(false);
      loadData();
    } catch (error) {
      toast.error('Failed to add entry');
    }
  };

  const handleUpdate = async (id, formData) => {
    try {
      await knowledgeApi.update(id, formData);
      toast.success('Entry updated');
      setEditingEntry(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update entry');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Deactivate this knowledge entry?')) return;
    try {
      await knowledgeApi.remove(id);
      toast.success('Entry deactivated');
      loadData();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleToggle = async (id) => {
    try {
      await knowledgeApi.toggle(id);
      setEntries(prev => prev.map(e => e.id === id ? { ...e, is_active: !e.is_active } : e));
      // Refresh stats
      const statsRes = await knowledgeApi.getStats();
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to toggle entry');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    fileInputRef.current.value = '';

    setUploadState('uploading');
    try {
      const res = await knowledgeApi.upload(file, uploadTechniqueId || null);
      const data = res.data;
      // If no technique was pre-selected but server detected one, auto-apply it
      let effectiveTechId = uploadTechniqueId || null;
      if (!effectiveTechId && data.suggested_technique_id) {
        effectiveTechId = data.suggested_technique_id;
        setUploadTechniqueId(data.suggested_technique_id);
      }
      setUploadState({
        ...data,
        technique_id: effectiveTechId,
        detected_technique: (!uploadTechniqueId && data.suggested_technique_id) ? {
          id: data.suggested_technique_id,
          name: data.suggested_technique_name
        } : null
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to process file');
      setUploadState(null);
    }
  };

  const handleSaveChunks = async () => {
    if (!uploadState || uploadState === 'uploading') return;
    try {
      await knowledgeApi.saveChunks({
        chunks: uploadState.chunks,
        fileName: uploadState.fileName,
        storedPath: uploadState.storedPath,
        technique_id: uploadState.technique_id || null
      });
      toast.success(`${uploadState.chunks.length} entries saved`);
      setUploadState(null);
      setUploadTechniqueId('');
      loadData();
    } catch (error) {
      toast.error('Failed to save entries');
    }
  };

  const updateChunk = (index, field, value) => {
    setUploadState(prev => ({
      ...prev,
      chunks: prev.chunks.map((c, i) => i === index ? { ...c, [field]: value } : c)
    }));
  };

  const handleTestSend = async () => {
    if (!testInput.trim() || testLoading) return;
    const userMsg = testInput.trim();
    setTestInput('');
    setTestMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setTestLoading(true);
    try {
      const res = testingEntry
        ? await knowledgeApi.testEntry(testingEntry.id, userMsg)
        : await knowledgeApi.test(userMsg);
      setTestMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.message,
        contextCount: res.data.contextCount,
        entryTitle: res.data.entryTitle
      }]);
    } catch (error) {
      setTestMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Error: Failed to get response. Please try again.',
        error: true
      }]);
    } finally {
      setTestLoading(false);
      setTimeout(() => testChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  const openEntryTest = (entry) => {
    if (testingEntry?.id === entry.id) {
      // Toggle off if same entry clicked again
      setTestingEntry(null);
      setTestMessages([]);
      setTestInput('');
      return;
    }
    setTestingEntry({ id: entry.id, title: entry.title });
    setTestMessages([]);
    setTestInput('');
    setShowTestChat(false); // close top panel, show inline instead
  };

  const removeChunk = (index) => {
    setUploadState(prev => ({
      ...prev,
      chunks: prev.chunks.filter((_, i) => i !== index)
    }));
  };

  // Filter & search
  const filteredEntries = entries.filter(e => {
    if (filterType !== 'all' && e.content_type !== filterType) return false;
    if (filterTechnique !== 'all') {
      if (filterTechnique === 'none') { if (e.technique_id) return false; }
      else if (e.technique_id !== filterTechnique) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return e.title.toLowerCase().includes(q) || e.content.toLowerCase().includes(q);
    }
    return true;
  });

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  // Upload review screen
  if (uploadState && uploadState !== 'uploading') {
    const handleReviewTechniqueChange = (newTechId) => {
      setUploadTechniqueId(newTechId);
      setUploadState(prev => ({ ...prev, technique_id: newTechId || null }));
    };

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <button onClick={() => setUploadState(null)} className="p-1 text-gray-500 hover:text-gray-700">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-lg font-semibold">Review Extracted Entries</h2>
              <p className="text-sm text-gray-500">
                From: {uploadState.fileName} — {uploadState.chunks.length} entries found
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={uploadState.technique_id || ''}
              onChange={(e) => handleReviewTechniqueChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">No Technique</option>
              {techniques.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <button onClick={() => setUploadState(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">
              Cancel
            </button>
            <button
              onClick={handleSaveChunks}
              disabled={uploadState.chunks.length === 0}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              Save All ({uploadState.chunks.length})
            </button>
          </div>
        </div>

        {uploadState.detected_technique && (
          <div className="flex items-center mb-4 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-xl text-sm text-indigo-800">
            <Brain className="w-4 h-4 mr-2 flex-shrink-0" />
            <span>Auto-detected technique: <strong>{uploadState.detected_technique.name}</strong> — you can change it using the dropdown above before saving.</span>
          </div>
        )}

        <div className="space-y-3">
          {uploadState.chunks.map((chunk, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 mr-4 space-y-2">
                  <input
                    type="text"
                    value={chunk.title}
                    onChange={(e) => updateChunk(idx, 'title', e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium"
                    placeholder="Entry title"
                  />
                  <select
                    value={chunk.content_type}
                    onChange={(e) => updateChunk(idx, 'content_type', e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded-lg text-xs"
                  >
                    {CONTENT_TYPES.map(ct => (
                      <option key={ct.value} value={ct.value}>{ct.label}</option>
                    ))}
                  </select>
                </div>
                <button onClick={() => removeChunk(idx)} className="p-1 text-gray-400 hover:text-red-600" title="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <textarea
                value={chunk.content}
                onChange={(e) => updateChunk(idx, 'content', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          ))}
          {uploadState.chunks.length === 0 && (
            <div className="text-center py-8 text-gray-500">All entries removed. Go back or upload a different file.</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Brain className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.totalEntries}</p>
                <p className="text-xs text-gray-500">Total Entries</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg"><Eye className="w-5 h-5 text-green-600" /></div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.activeEntries}</p>
                <p className="text-xs text-gray-500">Active Entries</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg"><Tag className="w-5 h-5 text-purple-600" /></div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.categories?.length || 0}</p>
                <p className="text-xs text-gray-500">Categories</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions & Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search entries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-64"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Categories</option>
            {CONTENT_TYPES.map(ct => (
              <option key={ct.value} value={ct.value}>{ct.label}</option>
            ))}
          </select>
          <select
            value={filterTechnique}
            onChange={(e) => setFilterTechnique(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Techniques</option>
            <option value="none">No Technique</option>
            {techniques.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <select
            value={uploadTechniqueId}
            onChange={(e) => setUploadTechniqueId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            title="Tag uploaded entries to a technique"
          >
            <option value="">No Technique</option>
            {techniques.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".pdf,.txt,.docx"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadState === 'uploading'}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploadState === 'uploading' ? 'Processing...' : 'Upload Document'}
          </button>
          <button
            onClick={() => { setShowAddForm(true); setEditingEntry(null); }}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Entry
          </button>
          <button
            onClick={() => { setTestingEntry(null); setTestMessages([]); setShowTestChat(!showTestChat); }}
            className={`flex items-center px-4 py-2 rounded-lg text-sm ${showTestChat && !testingEntry ? 'bg-green-600 text-white hover:bg-green-700' : 'border border-green-500 text-green-600 hover:bg-green-50'}`}
          >
            <Bot className="w-4 h-4 mr-2" />
            Test KB
          </button>
        </div>
      </div>

      {/* Upload spinner */}
      {uploadState === 'uploading' && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-4 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-sm text-blue-700">Processing document with AI... This may take a moment.</p>
        </div>
      )}

      {/* Test All KB Chat Panel (top-level, only when no specific entry is being tested) */}
      {showTestChat && !testingEntry && (
        <div className="bg-white rounded-xl border border-green-200 mb-4 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-green-50 border-b border-green-200">
            <div className="flex items-center space-x-2 min-w-0">
              <Bot className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span className="font-medium text-green-800 text-sm">Test All Knowledge Base</span>
              <span className="text-xs text-green-600">AI picks the most relevant entries</span>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              {testMessages.length > 0 && (
                <button
                  onClick={() => setTestMessages([])}
                  className="text-xs text-green-600 hover:text-green-800 px-2 py-1 rounded hover:bg-green-100"
                >
                  Clear
                </button>
              )}
              <button onClick={() => setShowTestChat(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="h-72 overflow-y-auto p-4 space-y-3" style={{ scrollBehavior: 'smooth' }}>
            {testMessages.length === 0 && (
              <div className="text-center text-gray-400 text-sm py-8">
                Type a question below to test how the AI responds using your knowledge base entries.
              </div>
            )}
            {testMessages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm ${
                  msg.role === 'user'
                    ? 'bg-primary-600 text-white'
                    : msg.error
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-gray-100 text-gray-800'
                }`}>
                  {msg.role === 'user' ? (
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  ) : (
                    <MarkdownRenderer content={msg.content} />
                  )}
                  {msg.contextCount !== undefined && (
                    <div className="text-xs mt-1.5 opacity-70">
                      {msg.contextCount > 0 ? `${msg.contextCount} KB entries matched` : 'No KB entries matched'}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {testLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-xl px-4 py-3">
                  <div className="flex space-x-1.5">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={testChatEndRef} />
          </div>

          <div className="flex items-center px-4 py-3 border-t border-gray-200 bg-gray-50">
            <input
              type="text"
              value={testInput}
              onChange={(e) => setTestInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleTestSend()}
              placeholder="Type a test question..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              disabled={testLoading}
            />
            <button
              onClick={handleTestSend}
              disabled={!testInput.trim() || testLoading}
              className="ml-2 p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || editingEntry) && (
        <KnowledgeEntryForm
          entry={editingEntry}
          techniques={techniques}
          onSave={editingEntry ? (data) => handleUpdate(editingEntry.id, data) : handleAdd}
          onCancel={() => { setShowAddForm(false); setEditingEntry(null); }}
        />
      )}

      {/* Entries List */}
      <div className="space-y-2">
        {filteredEntries.map((entry) => (
          <div key={entry.id} className={`bg-white rounded-xl border ${entry.is_active ? 'border-gray-200' : 'border-gray-200 opacity-60'}`}>
            <div
              onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                {expandedId === entry.id ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{entry.title}</p>
                  <div className="flex items-center space-x-2 mt-0.5">
                    {getTypeBadge(entry.content_type)}
                    {entry.technique_name && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 flex items-center">
                        <Link2 className="w-3 h-3 mr-1" />{entry.technique_name}
                      </span>
                    )}
                    {entry.source_file_name && (
                      <span className="text-xs text-gray-400 flex items-center">
                        <FileText className="w-3 h-3 mr-1" />{entry.source_file_name}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2 flex-shrink-0 ml-4" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => handleToggle(entry.id)}
                  title={entry.is_active ? 'Active — click to disable' : 'Inactive — click to enable'}
                  className={`p-1.5 rounded-lg transition-colors ${entry.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                >
                  {entry.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => openEntryTest(entry)}
                  className={`p-1.5 rounded-lg transition-colors ${testingEntry?.id === entry.id ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`}
                  title="Test this entry"
                >
                  <Play className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setEditingEntry(entry); setShowAddForm(false); }}
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(entry.id)}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {expandedId === entry.id && (
              <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{entry.content}</p>
                <p className="text-xs text-gray-400 mt-2">
                  Created: {new Date(entry.created_at).toLocaleDateString()}
                  {entry.updated_at && ` | Updated: ${new Date(entry.updated_at).toLocaleDateString()}`}
                </p>
              </div>
            )}

            {/* Inline test chat for this specific entry */}
            {testingEntry?.id === entry.id && (
              <div className="border-t border-green-200">
                <div className="flex items-center justify-between px-4 py-2.5 bg-green-50 border-b border-green-100">
                  <div className="flex items-center space-x-2 min-w-0">
                    <Bot className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className="font-medium text-green-800 text-sm truncate">Testing: {testingEntry.title}</span>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    {testMessages.length > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setTestMessages([]); }}
                        className="text-xs text-green-600 hover:text-green-800 px-2 py-1 rounded hover:bg-green-100"
                      >
                        Clear
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); setTestingEntry(null); setTestMessages([]); setTestInput(''); }} className="p-1 text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="h-60 overflow-y-auto p-4 space-y-3 bg-white" style={{ scrollBehavior: 'smooth' }}>
                  {testMessages.length === 0 && (
                    <div className="text-center text-gray-400 text-sm py-6">
                      Ask a question to test how the AI responds using "{testingEntry.title}"
                    </div>
                  )}
                  {testMessages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm ${
                        msg.role === 'user'
                          ? 'bg-primary-600 text-white'
                          : msg.error
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'bg-gray-100 text-gray-800'
                      }`}>
                        {msg.role === 'user' ? (
                          <div className="whitespace-pre-wrap">{msg.content}</div>
                        ) : (
                          <MarkdownRenderer content={msg.content} />
                        )}
                        {msg.contextCount !== undefined && (
                          <div className="text-xs mt-1.5 opacity-70">
                            {msg.contextCount > 0 ? `${msg.contextCount} KB entries matched` : 'No KB entries matched'}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {testLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 rounded-xl px-4 py-3">
                        <div className="flex space-x-1.5">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={testChatEndRef} />
                </div>

                <div className="flex items-center px-4 py-2.5 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                  <input
                    type="text"
                    value={testInput}
                    onChange={(e) => setTestInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleTestSend()}
                    placeholder={`Ask about "${testingEntry.title}"...`}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={testLoading}
                    autoFocus
                  />
                  <button
                    onClick={handleTestSend}
                    disabled={!testInput.trim() || testLoading}
                    className="ml-2 p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {filteredEntries.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
            {entries.length === 0
              ? 'No knowledge entries yet. Add entries manually or upload a document.'
              : 'No entries match your search or filter.'}
          </div>
        )}
      </div>
    </div>
  );
}

function KnowledgeEntryForm({ entry, techniques = [], onSave, onCancel }) {
  const [formData, setFormData] = useState({
    title: entry?.title || '',
    content: entry?.content || '',
    content_type: entry?.content_type || 'general',
    technique_id: entry?.technique_id || ''
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setIsSaving(true);
    try {
      await onSave({ ...formData, category: formData.content_type, technique_id: formData.technique_id || null });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-primary-200 p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">{entry ? 'Edit Entry' : 'Add New Entry'}</h3>
        <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex flex-wrap gap-3">
          <input
            type="text"
            placeholder="Entry title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm"
            required
          />
          <select
            value={formData.content_type}
            onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            {CONTENT_TYPES.map(ct => (
              <option key={ct.value} value={ct.value}>{ct.label}</option>
            ))}
          </select>
          <select
            value={formData.technique_id}
            onChange={(e) => setFormData({ ...formData, technique_id: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">No Technique</option>
            {techniques.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <textarea
          placeholder="Entry content — describe the technique, example, terminology, etc."
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          rows={5}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          required
        />
        <div className="flex justify-end space-x-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">
            Cancel
          </button>
          <button type="submit" disabled={isSaving} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm disabled:opacity-50">
            {isSaving ? 'Saving...' : (entry ? 'Update Entry' : 'Add Entry')}
          </button>
        </div>
      </form>
    </div>
  );
}

function ThemeTab({ config, onSave }) {
  const activePaletteId = config?.active_palette || 'memcoach-current';
  const [selectedId, setSelectedId] = useState(activePaletteId);
  const [isSaving, setIsSaving] = useState(false);

  const applyPaletteChoice = async (id) => {
    setSelectedId(id);
    setIsSaving(true);
    try {
      // Apply immediately for the admin (instant preview)
      setActivePalette(id);
      await adminApi.updateConfig({ active_palette: id });
      toast.success(`Palette "${PALETTES[id].name}" is now live for all users`);
      onSave && onSave();
    } catch (error) {
      toast.error('Failed to save palette');
      // Revert on failure
      setActivePalette(activePaletteId);
      setSelectedId(activePaletteId);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Site Colour Theme</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Click a palette to apply it site-wide. Change reflects on every student's next page load.
          Status colours (success green, error red) stay the same across all themes.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(PALETTES).map(([id, p]) => {
          const isActive = id === selectedId;
          return (
            <button
              key={id}
              onClick={() => !isSaving && applyPaletteChoice(id)}
              disabled={isSaving}
              className={`text-left rounded-xl overflow-hidden border-2 transition-all disabled:opacity-60 ${
                isActive
                  ? 'border-primary-600 shadow-lg ring-2 ring-primary-200 dark:ring-primary-900'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
              style={{ background: p.paper }}
            >
              {/* Swatch band */}
              <div className="flex h-12">
                <div className="flex-1" style={{ background: p.primary }} />
                <div className="flex-1" style={{ background: p.accent }} />
                <div className="flex-1" style={{ background: p.paper }} />
                <div className="flex-1" style={{ background: p.ink }} />
              </div>
              {/* Info */}
              <div className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-semibold text-sm" style={{ color: p.ink }}>{p.name}</p>
                  {isActive && (
                    <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: p.primary, color: p.paper }}>
                      <Check className="w-3 h-3 mr-1" /> Active
                    </span>
                  )}
                </div>
                <p className="text-xs italic" style={{ color: p.ink, opacity: 0.65 }}>{p.tagline}</p>
                {/* Mini button preview */}
                <div className="flex gap-2 mt-3">
                  <span className="px-2.5 py-1 text-[11px] font-semibold rounded" style={{ background: p.primary, color: p.paper }}>Start</span>
                  <span className="px-2.5 py-1 text-[11px] font-semibold rounded" style={{ background: p.accent, color: p.ink }}>Recharge</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <p className="mt-6 text-xs text-gray-400 dark:text-gray-500">
        Tip: only the <strong>primary brand colour</strong> (buttons, links, sidebar) changes. Backgrounds and status colours stay neutral so students always recognise success/error states.
      </p>
    </div>
  );
}

function SettingsTab({ config, onSave }) {
  const [formData, setFormData] = useState(config);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await adminApi.updateConfig(formData);
      toast.success('Settings saved');
      onSave();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-xl">
      <h2 className="text-lg font-semibold mb-4">Platform Configuration</h2>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Onboarding Fee (₹)
          </label>
          <input
            type="number"
            value={formData.onboarding_fee || ''}
            onChange={(e) => setFormData({ ...formData, onboarding_fee: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tokens %</label>
            <input
              type="number"
              value={formData.knobly_share_percent || ''}
              onChange={(e) => setFormData({ ...formData, knobly_share_percent: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trainer %</label>
            <input
              type="number"
              value={formData.trainer_share_percent || ''}
              onChange={(e) => setFormData({ ...formData, trainer_share_percent: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deepak %</label>
            <input
              type="number"
              value={formData.deepak_share_percent || ''}
              onChange={(e) => setFormData({ ...formData, deepak_share_percent: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">L1 Trial Days</label>
            <input
              type="number"
              value={formData.l1_trial_days || ''}
              onChange={(e) => setFormData({ ...formData, l1_trial_days: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">L1 Daily Limit</label>
            <input
              type="number"
              value={formData.l1_daily_limit || ''}
              onChange={(e) => setFormData({ ...formData, l1_daily_limit: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        <div className="border-t pt-4 mt-4">
          <h3 className="text-md font-semibold mb-3">Custom Top-up Settings</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Amount (₹)</label>
              <input
                type="number"
                value={formData.custom_recharge_min_amount || ''}
                onChange={(e) => setFormData({ ...formData, custom_recharge_min_amount: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Amount (₹)</label>
              <input
                type="number"
                value={formData.custom_recharge_max_amount || ''}
                onChange={(e) => setFormData({ ...formData, custom_recharge_max_amount: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Questions per ₹</label>
              <input
                type="number"
                step="0.1"
                value={formData.custom_recharge_rate || ''}
                onChange={(e) => setFormData({ ...formData, custom_recharge_rate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Enabled</label>
              <select
                value={formData.custom_recharge_enabled || 'true'}
                onChange={(e) => setFormData({ ...formData, custom_recharge_enabled: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

function ApiBillingLedger({ billing, onChange }) {
  const today = new Date().toISOString().split('T')[0];
  const [topupAmt, setTopupAmt] = useState('');
  const [topupDate, setTopupDate] = useState(today);
  const [spendAmt, setSpendAmt] = useState('');
  const [spendDate, setSpendDate] = useState(today);
  const [saving, setSaving] = useState(false);

  const addEntry = async (type, amount, date) => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSaving(true);
    try {
      await adminApi.addApiBillingEntry({
        event_type: type,
        event_date: date,
        amount_inr: parseFloat(amount),
      });
      toast.success(type === 'topup' ? 'Top-up logged' : 'Spend logged');
      if (type === 'topup') setTopupAmt('');
      else setSpendAmt('');
      onChange && onChange();
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (id) => {
    if (!confirm('Delete this entry?')) return;
    try {
      await adminApi.deleteApiBillingEntry(id);
      toast.success('Deleted');
      onChange && onChange();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const fmt = (n) => '₹' + (parseFloat(n) || 0).toFixed(2);
  const summary = billing?.summary || { total_topup: 0, total_spend: 0, balance: 0 };
  const events = billing?.events || [];

  return (
    <div className="bg-white rounded-xl border-2 border-primary-200 p-5">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900">New API Billing Ledger (Manual)</h3>
        <p className="text-xs text-gray-500 mt-1">
          Log every top-up you make on the Google Cloud project, and the monthly spend that Google's dashboard reports. Balance = topups minus spend.
        </p>
      </div>

      {/* Balance summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500">Total Topped Up</div>
          <div className="text-xl font-bold text-gray-900">{fmt(summary.total_topup)}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500">Total Spent (per Google)</div>
          <div className="text-xl font-bold text-gray-900">{fmt(summary.total_spend)}</div>
        </div>
        <div className={`rounded-lg p-3 ${summary.balance >= 500 ? 'bg-green-50' : 'bg-amber-50'}`}>
          <div className="text-xs text-gray-500">Estimated Balance</div>
          <div className={`text-xl font-bold ${summary.balance >= 500 ? 'text-green-700' : 'text-amber-700'}`}>{fmt(summary.balance)}</div>
        </div>
      </div>

      {/* Add topup */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div className="border border-gray-200 rounded-lg p-3">
          <div className="text-xs font-semibold text-primary-700 mb-2">+ Log a Top-up</div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={topupDate}
              onChange={(e) => setTopupDate(e.target.value)}
              className="px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
            <div className="relative flex-1">
              <span className="absolute left-2 top-1.5 text-gray-400 text-sm">₹</span>
              <input
                type="number"
                min="0"
                step="1"
                value={topupAmt}
                onChange={(e) => setTopupAmt(e.target.value)}
                placeholder="Amount"
                className="w-full pl-6 pr-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
            <button
              onClick={() => addEntry('topup', topupAmt, topupDate)}
              disabled={saving}
              className="px-3 py-1.5 bg-primary-600 text-white rounded text-sm font-medium disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-3">
          <div className="text-xs font-semibold text-gray-700 mb-2">+ Log Google-actual Spend</div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={spendDate}
              onChange={(e) => setSpendDate(e.target.value)}
              className="px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
            <div className="relative flex-1">
              <span className="absolute left-2 top-1.5 text-gray-400 text-sm">₹</span>
              <input
                type="number"
                min="0"
                step="1"
                value={spendAmt}
                onChange={(e) => setSpendAmt(e.target.value)}
                placeholder="Amount"
                className="w-full pl-6 pr-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
            <button
              onClick={() => addEntry('spend', spendAmt, spendDate)}
              disabled={saving}
              className="px-3 py-1.5 bg-gray-700 text-white rounded text-sm font-medium disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Events list */}
      {events.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-600">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium text-right">Amount</th>
                <th className="px-3 py-2 font-medium">Notes</th>
                <th className="px-3 py-2 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {events.map(e => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-700 text-xs">{new Date(e.event_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${e.event_type === 'topup' ? 'bg-primary-100 text-primary-700' : 'bg-gray-200 text-gray-700'}`}>
                      {e.event_type}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-gray-900">{fmt(e.amount_inr)}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{e.notes || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => removeEntry(e.id)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-center text-sm text-gray-400 py-4">No entries yet. Log your first top-up above.</p>
      )}
    </div>
  );
}

function UsageAnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('all');

  useEffect(() => {
    adminApi.getUsageAnalytics()
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load usage analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!data) return <div className="text-gray-500 text-center py-8">No data available</div>;

  const { summary, averages, monthly, models, top_students, by_api } = data;

  const fmtNum = (n) => (n || 0).toLocaleString();
  const fmtUsd = (n) => '$' + (n || 0).toFixed(4);
  const fmtInr = (n) => '₹' + (n || 0).toFixed(2);

  return (
    <div className="space-y-6">
      {/* Month picker — filter monthly spend view */}
      {monthly && monthly.length > 0 && (() => {
        // Deduplicate months since `monthly` has one row per model per month
        const monthMap = {};
        for (const row of monthly) {
          if (!monthMap[row.month]) {
            monthMap[row.month] = { month: row.month, questions: 0, responses: 0, input_tokens: 0, output_tokens: 0, cost_usd: 0, cost_inr: 0 };
          }
          const m = monthMap[row.month];
          m.questions += parseInt(row.questions || 0);
          m.responses += parseInt(row.responses || 0);
          m.input_tokens += parseInt(row.input_tokens || 0);
          m.output_tokens += parseInt(row.output_tokens || 0);
          m.cost_usd += parseFloat(row.cost_usd || 0);
          m.cost_inr += parseFloat(row.cost_inr || 0);
        }
        const months = Object.values(monthMap).sort((a, b) => b.month.localeCompare(a.month));
        const selected = selectedMonth === 'all'
          ? months.reduce((acc, m) => ({
              month: 'All months',
              questions: acc.questions + m.questions,
              responses: acc.responses + m.responses,
              input_tokens: acc.input_tokens + m.input_tokens,
              output_tokens: acc.output_tokens + m.output_tokens,
              cost_usd: acc.cost_usd + m.cost_usd,
              cost_inr: acc.cost_inr + m.cost_inr,
            }), { questions: 0, responses: 0, input_tokens: 0, output_tokens: 0, cost_usd: 0, cost_inr: 0 })
          : months.find(m => m.month === selectedMonth);
        const label = selectedMonth === 'all'
          ? 'All months combined'
          : new Date(selectedMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-primary-200 dark:border-primary-800 p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Monthly Spend Viewer</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Pick a month to see its exact totals</p>
              </div>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm font-medium min-w-[200px]"
              >
                <option value="all">All months combined</option>
                {months.map(m => (
                  <option key={m.month} value={m.month}>
                    {new Date(m.month + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                  </option>
                ))}
              </select>
            </div>

            {selected && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Period</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">{label}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Questions</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">{fmtNum(selected.questions)}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total tokens</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">{fmtNum(selected.input_tokens + selected.output_tokens)}</div>
                </div>
                <div className="bg-primary-50 dark:bg-primary-900/30 rounded-lg p-4 border border-primary-200 dark:border-primary-800">
                  <div className="text-xs text-primary-700 dark:text-primary-300 mb-1 font-medium">Total spent</div>
                  <div className="text-xl font-bold text-primary-700 dark:text-primary-200">{fmtInr(selected.cost_inr)}</div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">≈ {fmtUsd(selected.cost_usd)}</div>
                </div>
              </div>
            )}
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3 italic">
              Calculated from logged tokens × Gemini pricing ($0.30/M in, $2.50/M out) × ₹84.5/USD. Reconciles within ~5% of Google's bill.
            </p>
          </div>
        );
      })()}

      {/* API Key Era Split — old project vs new project */}
      {by_api && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Usage by API Key (switchover: {by_api.switchover_date})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border-2 border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-gray-700">Old API</div>
                <span className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">before {by_api.switchover_date}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-500">Questions</div>
                  <div className="text-lg font-bold text-gray-900">{fmtNum(by_api.old_api.questions)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Total Tokens</div>
                  <div className="text-lg font-bold text-gray-900">{fmtNum(by_api.old_api.input_tokens + by_api.old_api.output_tokens)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Cost (USD)</div>
                  <div className="text-lg font-bold text-gray-900">{fmtUsd(by_api.old_api.cost_usd)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Cost (INR)</div>
                  <div className="text-lg font-bold text-gray-900">{fmtInr(by_api.old_api.cost_inr)}</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border-2 border-primary-300 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-primary-700">New API</div>
                <span className="text-[10px] uppercase tracking-wider bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">from {by_api.switchover_date} · CURRENT</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-500">Questions</div>
                  <div className="text-lg font-bold text-gray-900">{fmtNum(by_api.new_api.questions)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Total Tokens</div>
                  <div className="text-lg font-bold text-gray-900">{fmtNum(by_api.new_api.input_tokens + by_api.new_api.output_tokens)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Cost (USD)</div>
                  <div className="text-lg font-bold text-gray-900">{fmtUsd(by_api.new_api.cost_usd)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Cost (INR)</div>
                  <div className="text-lg font-bold text-gray-900">{fmtInr(by_api.new_api.cost_inr)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-sm text-gray-500 mb-1">Total Questions</div>
          <div className="text-2xl font-bold text-gray-900">{fmtNum(summary.total_questions)}</div>
          <div className="text-xs text-gray-400 mt-1">{fmtNum(summary.total_responses)} AI responses</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-sm text-gray-500 mb-1">Total Conversations</div>
          <div className="text-2xl font-bold text-gray-900">{fmtNum(summary.total_conversations)}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-sm text-gray-500 mb-1">Total Tokens</div>
          <div className="text-2xl font-bold text-gray-900">{fmtNum(summary.total_input_tokens + summary.total_output_tokens)}</div>
          <div className="text-xs text-gray-400 mt-1">In: {fmtNum(summary.total_input_tokens)} · Out: {fmtNum(summary.total_output_tokens)}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-sm text-gray-500 mb-1">Total API Cost</div>
          <div className="text-2xl font-bold text-gray-900">{fmtUsd(summary.total_cost_usd)}</div>
          <div className="text-xs text-gray-400 mt-1">{fmtInr(summary.total_cost_inr)}</div>
        </div>
      </div>

      {/* Per-Question Averages */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Per-Response Averages</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <div className="text-xs text-gray-500">Avg Input Tokens</div>
            <div className="text-lg font-semibold text-gray-900">{fmtNum(averages.avg_input)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Avg Output Tokens</div>
            <div className="text-lg font-semibold text-gray-900">{fmtNum(averages.avg_output)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Avg Total Tokens</div>
            <div className="text-lg font-semibold text-gray-900">{fmtNum(averages.avg_total)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Cost/Response (USD)</div>
            <div className="text-lg font-semibold text-gray-900">{fmtUsd(averages.cost_per_question_usd)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Cost/Response (INR)</div>
            <div className="text-lg font-semibold text-gray-900">{fmtInr(averages.cost_per_question_inr)}</div>
          </div>
        </div>
      </div>

      {/* Monthly Usage Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">Monthly Usage</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-600">
                <th className="px-5 py-3 font-medium">Month</th>
                <th className="px-5 py-3 font-medium text-right">Questions</th>
                <th className="px-5 py-3 font-medium text-right">Responses</th>
                <th className="px-5 py-3 font-medium text-right">Input Tokens</th>
                <th className="px-5 py-3 font-medium text-right">Output Tokens</th>
                <th className="px-5 py-3 font-medium text-right">Cost (USD)</th>
                <th className="px-5 py-3 font-medium text-right">Cost (INR)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {monthly.map(row => (
                <tr key={row.month} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium text-gray-900">{row.month}</td>
                  <td className="px-5 py-3 text-right text-gray-700">{fmtNum(row.questions)}</td>
                  <td className="px-5 py-3 text-right text-gray-700">{fmtNum(row.responses)}</td>
                  <td className="px-5 py-3 text-right text-gray-700">{fmtNum(row.input_tokens)}</td>
                  <td className="px-5 py-3 text-right text-gray-700">{fmtNum(row.output_tokens)}</td>
                  <td className="px-5 py-3 text-right text-gray-700">{fmtUsd(row.cost_usd)}</td>
                  <td className="px-5 py-3 text-right text-gray-700">{fmtInr(row.cost_inr)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-semibold">
                <td className="px-5 py-3 text-gray-900">Total</td>
                <td className="px-5 py-3 text-right text-gray-900">{fmtNum(monthly.reduce((s, r) => s + r.questions, 0))}</td>
                <td className="px-5 py-3 text-right text-gray-900">{fmtNum(monthly.reduce((s, r) => s + r.responses, 0))}</td>
                <td className="px-5 py-3 text-right text-gray-900">{fmtNum(monthly.reduce((s, r) => s + r.input_tokens, 0))}</td>
                <td className="px-5 py-3 text-right text-gray-900">{fmtNum(monthly.reduce((s, r) => s + r.output_tokens, 0))}</td>
                <td className="px-5 py-3 text-right text-gray-900">{fmtUsd(monthly.reduce((s, r) => s + r.cost_usd, 0))}</td>
                <td className="px-5 py-3 text-right text-gray-900">{fmtInr(monthly.reduce((s, r) => s + r.cost_inr, 0))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Bottom Row: Model Breakdown + Top Students */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Model Breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">Model Breakdown</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-600">
                  <th className="px-5 py-3 font-medium">Model</th>
                  <th className="px-5 py-3 font-medium text-right">Responses</th>
                  <th className="px-5 py-3 font-medium text-right">Input</th>
                  <th className="px-5 py-3 font-medium text-right">Output</th>
                  <th className="px-5 py-3 font-medium text-right">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {models.map(row => (
                  <tr key={row.model} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-medium text-gray-900">{row.model}</td>
                    <td className="px-5 py-3 text-right text-gray-700">{fmtNum(row.responses)}</td>
                    <td className="px-5 py-3 text-right text-gray-700">{fmtNum(row.input_tokens)}</td>
                    <td className="px-5 py-3 text-right text-gray-700">{fmtNum(row.output_tokens)}</td>
                    <td className="px-5 py-3 text-right text-gray-700">{fmtUsd(row.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Students */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">Top 15 Students by Token Usage</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-600">
                  <th className="px-5 py-3 font-medium">Student</th>
                  <th className="px-5 py-3 font-medium text-right">Questions</th>
                  <th className="px-5 py-3 font-medium text-right">Tokens</th>
                  <th className="px-5 py-3 font-medium text-right">Last Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {top_students.map((s, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <div className="font-medium text-gray-900">{s.name}</div>
                      <div className="text-xs text-gray-400">{s.email}</div>
                    </td>
                    <td className="px-5 py-3 text-right text-gray-700">{fmtNum(s.questions)}</td>
                    <td className="px-5 py-3 text-right text-gray-700">{fmtNum(s.total_tokens)}</td>
                    <td className="px-5 py-3 text-right text-gray-500 text-xs">
                      {s.last_active ? new Date(s.last_active).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReferralsTab() {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewStudents, setViewStudents] = useState(null);
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({ code: '', total_questions: 50, trial_days: 15, max_uses: '', expires_at: '' });

  useEffect(() => { loadReferrals(); }, []);

  const loadReferrals = async () => {
    try {
      const res = await adminApi.getReferrals();
      setReferrals(res.data);
    } catch (err) {
      toast.error('Failed to load referral codes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await adminApi.createReferral({
        code: form.code,
        total_questions: parseInt(form.total_questions) || 50,
        trial_days: parseInt(form.trial_days) || 15,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at || null
      });
      toast.success('Referral code created');
      setShowCreate(false);
      setForm({ code: '', total_questions: 50, trial_days: 15, max_uses: '', expires_at: '' });
      loadReferrals();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create');
    }
  };

  const handleUpdate = async () => {
    try {
      await adminApi.updateReferral(editId, {
        total_questions: parseInt(form.total_questions) || 50,
        trial_days: parseInt(form.trial_days) || 15,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at || null
      });
      toast.success('Referral code updated');
      setEditId(null);
      loadReferrals();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
  };

  const handleToggle = async (ref) => {
    try {
      await adminApi.updateReferral(ref.id, { is_active: !ref.is_active });
      toast.success(ref.is_active ? 'Deactivated' : 'Activated');
      loadReferrals();
    } catch (err) {
      toast.error('Failed to toggle');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this referral code?')) return;
    try {
      await adminApi.deleteReferral(id);
      toast.success('Deleted');
      loadReferrals();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleViewStudents = async (ref) => {
    setViewStudents(ref);
    try {
      const res = await adminApi.getReferralStudents(ref.id);
      setStudents(res.data);
    } catch (err) {
      toast.error('Failed to load students');
    }
  };

  const startEdit = (ref) => {
    setEditId(ref.id);
    setForm({
      code: ref.code,
      total_questions: ref.total_questions,
      trial_days: ref.trial_days,
      max_uses: ref.max_uses || '',
      expires_at: ref.expires_at ? ref.expires_at.slice(0, 10) : ''
    });
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/register?code=${code}`;
    navigator.clipboard.writeText(url);
    toast.success('Registration link copied!');
  };

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Referral Codes</h2>
        <button onClick={() => { setShowCreate(true); setForm({ code: '', total_questions: 50, trial_days: 15, max_uses: '', expires_at: '' }); }}
          className="flex items-center gap-1 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700">
          <Plus className="w-4 h-4" /> New Code
        </button>
      </div>

      <p className="text-sm text-gray-500">
        Create referral codes to give students boosted trials (more questions, longer duration). Share the registration link with the code.
      </p>

      {/* Create / Edit Modal */}
      {(showCreate || editId) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl p-6">
            <h3 className="text-lg font-semibold mb-4">{editId ? 'Edit Referral Code' : 'Create Referral Code'}</h3>
            <div className="space-y-3">
              {!editId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input type="text" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="e.g. OWNER50" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Questions</label>
                  <input type="number" value={form.total_questions} onChange={e => setForm({ ...form, total_questions: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trial Days</label>
                  <input type="number" value={form.trial_days} onChange={e => setForm({ ...form, trial_days: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses (blank = unlimited)</label>
                  <input type="number" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value })}
                    placeholder="Unlimited" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expires On (optional)</label>
                  <input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500" />
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => { setShowCreate(false); setEditId(null); }}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={editId ? handleUpdate : handleCreate}
                className="flex-1 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700">
                {editId ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Students Modal */}
      {viewStudents && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Students using <span className="text-primary-600">{viewStudents.code}</span></h3>
              <button onClick={() => setViewStudents(null)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            {students.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">No students have used this code yet.</p>
            ) : (
              <div className="space-y-2">
                {students.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{s.name}</p>
                      <p className="text-xs text-gray-500">{s.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{new Date(s.created_at).toLocaleDateString()}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${s.converted_to_l2 ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {s.converted_to_l2 ? 'Paid' : s.tier}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Referral codes list */}
      {referrals.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No referral codes yet. Create one to get started.</div>
      ) : (
        <div className="space-y-3">
          {referrals.map(ref => (
            <div key={ref.id} className={`bg-white border rounded-xl p-4 ${!ref.is_active ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-primary-600">{ref.code}</span>
                    {!ref.is_active && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Inactive</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                    <span>{ref.total_questions} total questions</span>
                    <span>{ref.trial_days} days trial</span>
                    <span>{ref.used_count}{ref.max_uses ? `/${ref.max_uses}` : ''} used</span>
                  </div>
                  {ref.expires_at && (
                    <p className="text-xs text-gray-400 mt-1">
                      Expires: {new Date(ref.expires_at).toLocaleDateString()}
                      {new Date(ref.expires_at) < new Date() && <span className="text-red-500 ml-1">(expired)</span>}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => copyLink(ref.code)} title="Copy registration link"
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-primary-600">
                    <Link2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleViewStudents(ref)} title="View students"
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-primary-600">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button onClick={() => startEdit(ref)} title="Edit"
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-primary-600">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleToggle(ref)} title={ref.is_active ? 'Deactivate' : 'Activate'}
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-primary-600">
                    {ref.is_active ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4" />}
                  </button>
                  <button onClick={() => handleDelete(ref.id)} title="Delete"
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
